import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaceIcon from '@mui/icons-material/Place';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WatchIcon from '@mui/icons-material/Watch';
import BuildIcon from '@mui/icons-material/Build';
import { uploadRunningRaceGpx, deleteRunningRaceGpx, deleteRunningRace, importRunningRaces, adminListUsers, adminGetBetaHistory, adminReanalyzeAll, adminAuditCheckpoints, adminListUserFiles, adminDownloadUserFile, adminUserStats } from '../../api/runningApi';
import FitFileCombiner from '../../components/running/FitFileCombiner';
import WorkoutPickerDrawer from '../../components/running/WorkoutPickerDrawer';

/** Seconds to h:mm:ss, for durations shown in the admin grids. */
const formatHms = (sec) => {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// How each location in the Users grid was arrived at. The backend answers from
// the strongest source it has and names it; "where they signed in from" and
// "where they last ran" are different claims, so the column says which.
const LOCATION_SOURCES = {
  ip:     { tag: 'ip',    hint: 'IP address of the most recent session we could place.' },
  run:    { tag: 'run',   hint: 'Where their most recent run was recorded.' },
  nearby: { tag: '≈ run', hint: "Their last run's coordinates had no place name, so the closest named run we hold supplied one — the town may be a neighbouring one." },
  race:   { tag: '≈ race', hint: 'The last race they ran a prediction for. Where they went, not necessarily where they live.' },
  email:  { tag: '? mail', hint: 'Guessed from the country-code domain of their email address. Nothing better was available.' },
};

const RunningAdmin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [selectedRace, setSelectedRace] = useState(null);
  const [racesDrawerOpen, setRacesDrawerOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Import races state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Beta-history inspector state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [betaRows, setBetaRows] = useState([]);
  const [betaLoading, setBetaLoading] = useState(false);

  // Re-analysis of stored sessions. A stored session is the parser's output from
  // when it ran, so a parser change is invisible until this is done.
  // Every account on one screen: who they are, where they are, what they have
  // done. One request — the aggregation happens in SQL.
  // Which group of panels is on screen. Remembered, because an admin running a
  // re-analysis and then reloading should land back on Maintenance rather than
  // on the first tab every time.
  const [adminTab, setAdminTab] = useState(
    () => localStorage.getItem('runningAdminTab') || 'users'
  );
  useEffect(() => { localStorage.setItem('runningAdminTab', adminTab); }, [adminTab]);

  const [statsRows, setStatsRows] = useState([]);
  const [statsTotals, setStatsTotals] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // One user's stored FIT files, so a reported problem can be reproduced from
  // the actual file instead of a description of it.
  const [filesUser, setFilesUser] = useState(null);
  const [userFiles, setUserFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [reanalyzeDryRun, setReanalyzeDryRun] = useState(true);
  const [reanalyzeLimit, setReanalyzeLimit] = useState('');
  const [reanalyzeOffset, setReanalyzeOffset] = useState('');
  const [reanalyzeLoading, setReanalyzeLoading] = useState(false);
  const [reanalyzeResult, setReanalyzeResult] = useState(null);

  // Checkpoint coordinate audit against each race's own GPX.
  const [auditDryRun, setAuditDryRun] = useState(true);
  const [auditDropGpx, setAuditDropGpx] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  const handleAuditCheckpoints = async () => {
    if (!auditDryRun && !window.confirm(
      'Snap the flagged checkpoint coordinates onto each race\'s GPX track?\n\n'
      + 'This rewrites checkpoint lat/lon for every race the audit flagged, and cannot be undone. '
      + 'Take a database backup of running_races first.'
    )) return;
    setAuditLoading(true);
    setAuditResult(null);
    try {
      const data = await adminAuditCheckpoints({ dryRun: auditDryRun, dropBadGpx: auditDropGpx });
      setAuditResult(data);
      setSnackbar({
        open: true, severity: 'success',
        message: `${data.dry_run ? 'Dry run: ' : ''}${data.checkpoints_snapped} checkpoint(s) `
          + `across ${data.races_with_fixes} race(s); ${data.races_clean}/${data.races_audited} clean`,
      });
    } catch (err) {
      setSnackbar({
        open: true, severity: 'error',
        message: err?.response?.data?.detail || 'Checkpoint audit failed',
      });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleSelectFilesUser = async (user) => {
    setFilesUser(user);
    setUserFiles([]);
    if (!user) return;
    setFilesLoading(true);
    try {
      const data = await adminListUserFiles(user.id);
      setUserFiles(data.files || []);
    } catch (err) {
      setSnackbar({
        open: true, severity: 'error',
        message: err?.response?.data?.detail || 'Could not load that user\u2019s files',
      });
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDownloadUserFile = async (file) => {
    setDownloadingId(file.id);
    try {
      const response = await adminDownloadUserFile(filesUser.id, file.id);
      // Hand the blob to the browser through a throwaway anchor. revokeObjectURL
      // matters here: these are up to 12 MB and would otherwise be held until a
      // reload.
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || `running_${file.id}.fit`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({
        open: true, severity: 'error',
        message: err?.response?.data?.detail || 'Download failed',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleReanalyzeAll = async () => {
    if (!reanalyzeDryRun) {
      const from = reanalyzeOffset ? Number(reanalyzeOffset) : 0;
      const n = reanalyzeLimit
        ? `${reanalyzeLimit} stored sessions starting at ${from}`
        : (from ? `EVERY stored session from ${from} onwards` : 'EVERY stored session');
      if (!window.confirm(
        `Re-analyse ${n} for ALL users and overwrite the saved results?\n\n`
        + 'This cannot be undone. Run a dry run first to see what would change.'
      )) return;
    }
    setReanalyzeLoading(true);
    setReanalyzeResult(null);
    try {
      const data = await adminReanalyzeAll({
        dryRun: reanalyzeDryRun,
        limit: reanalyzeLimit ? Number(reanalyzeLimit) : null,
        offset: reanalyzeOffset ? Number(reanalyzeOffset) : 0,
      });
      setReanalyzeResult(data);
      // Move the box on to where this batch stopped, so the next press carries
      // straight on. Null means the end of the set; go back to the start.
      setReanalyzeOffset(data.next_offset == null ? '' : String(data.next_offset));
      setSnackbar({
        open: true,
        severity: data.failed > 0 ? 'warning' : 'success',
        message: `${data.dry_run ? 'Dry run: ' : ''}${data.reanalyzed} re-analysed, `
          + `${data.failed} failed, ${data.skipped_no_file} skipped`
          + (data.next_offset == null ? '' : ` — ${data.total_matching - data.next_offset} left`),
      });
    } catch (err) {
      setSnackbar({
        open: true, severity: 'error',
        message: err?.response?.data?.detail || 'Re-analysis failed',
      });
    } finally {
      setReanalyzeLoading(false);
    }
  };

  const loadUserStats = () => {
    setStatsLoading(true);
    adminUserStats()
      .then((d) => {
        setStatsRows((d?.users || []).map((u) => ({ ...u, id: u.id })));
        setStatsTotals(d?.totals || null);
      })
      .catch(() => {
        setStatsRows([]); setStatsTotals(null);
        setSnackbar({ open: true, severity: 'error',
                      message: 'Failed to load user statistics' });
      })
      .finally(() => setStatsLoading(false));
  };

  useEffect(() => {
    adminListUsers().then(setUsers).catch(() => setUsers([]));
    loadUserStats();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectBetaUser = async (user) => {
    setSelectedUser(user);
    setBetaRows([]);
    if (!user) return;
    setBetaLoading(true);
    try {
      setBetaRows(await adminGetBetaHistory(user.id));
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load beta history', severity: 'error' });
    } finally {
      setBetaLoading(false);
    }
  };

  const betaColumns = [
    { field: 'created_at', headerName: 'Date', width: 160,
      valueFormatter: (v) => (v ? new Date(v).toLocaleString() : '') },
    { field: 'mode', headerName: 'Mode', width: 130 },
    { field: 'race_name', headerName: 'Race', width: 160,
      valueGetter: (v, row) => row.race_name || (row.race_id ? `#${row.race_id}` : '—') },
    { field: 'beta_used', headerName: 'β used', width: 90,
      valueFormatter: (v) => (v != null ? Number(v).toFixed(4) : '') },
    { field: 'beta_raw', headerName: 'β raw', width: 90,
      valueFormatter: (v) => (v != null ? Number(v).toFixed(4) : '—') },
    { field: 'status', headerName: 'Fit', width: 110, sortable: false,
      renderCell: (p) => {
        const label = p.row.was_default ? (p.row.was_clamped ? 'clamped' : 'default') : 'fitted';
        const color = p.row.was_default ? (p.row.was_clamped ? 'error' : 'default') : 'success';
        return <Chip size="small" label={label} color={color} variant="outlined" />;
      } },
    { field: 'r_squared', headerName: 'R²', width: 80,
      valueFormatter: (v) => (v != null ? Number(v).toFixed(3) : '—') },
    { field: 'n_references', headerName: 'Refs', width: 70 },
    { field: 'span', headerName: 'Dist span (km)', width: 130, sortable: false,
      valueGetter: (v, row) => (row.distance_min_km != null && row.distance_max_km != null)
        ? `${Math.round(row.distance_min_km)}–${Math.round(row.distance_max_km)}` : '—' },
    { field: 'reference_files', headerName: 'Reference files', flex: 1, minWidth: 240, sortable: false,
      valueGetter: (v) => (Array.isArray(v) ? v.join(', ') : '') },
  ];

  const handleSelectRace = (source) => {
    setSelectedRace(source.record);
    setRacesDrawerOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.gpx')) {
      setUploadFile(file);
    } else {
      setSnackbar({ open: true, message: 'Please select a valid GPX file', severity: 'error' });
      event.target.value = '';
    }
  };

  const handleUploadGpx = async () => {
    if (!selectedRace || !uploadFile) {
      setSnackbar({ open: true, message: 'Please select a race and a GPX file', severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      await uploadRunningRaceGpx(selectedRace.id, uploadFile);
      setSnackbar({ open: true, message: 'GPX file uploaded successfully!', severity: 'success' });
      setUploadFile(null);
      const fileInput = document.getElementById('gpx-file-input');
      if (fileInput) fileInput.value = '';
      setSelectedRace(prev => ({ ...prev, has_gpx: true }));
    } catch (error) {
      console.error('Failed to upload GPX:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to upload GPX file',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGpx = async () => {
    if (!selectedRace) {
      setSnackbar({ open: true, message: 'Please select a race', severity: 'warning' });
      return;
    }

    if (!selectedRace.has_gpx) {
      setSnackbar({ open: true, message: 'This race has no GPX file to delete', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the GPX file for ${selectedRace.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteRunningRaceGpx(selectedRace.id);
      setSnackbar({ open: true, message: 'GPX file deleted successfully!', severity: 'success' });
      setSelectedRace(prev => ({ ...prev, has_gpx: false }));
    } catch (error) {
      console.error('Failed to delete GPX:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete GPX file',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRace = async () => {
    if (!selectedRace) {
      setSnackbar({ open: true, message: 'Please select a race', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete the race "${selectedRace.name}" (${selectedRace.event_name})? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteRunningRace(selectedRace.id);
      setSnackbar({ open: true, message: `Race "${selectedRace.name}" deleted successfully!`, severity: 'success' });
      setSelectedRace(null);
    } catch (error) {
      console.error('Failed to delete race:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete race',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.json')) {
      setImportFile(file);
      setImportResult(null);
    } else {
      setSnackbar({ open: true, message: 'Please select a valid JSON file', severity: 'error' });
      event.target.value = '';
    }
  };

  const handleImportRaces = async () => {
    if (!importFile) {
      setSnackbar({ open: true, message: 'Please select a JSON file', severity: 'warning' });
      return;
    }

    try {
      setImportLoading(true);
      setImportResult(null);
      const result = await importRunningRaces(importFile);
      setImportResult(result);
      setImportFile(null);
      const fileInput = document.getElementById('import-json-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Failed to import races:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to import races',
        severity: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      <Box mb={4}>
        <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold" color={colors.grey[100]} mb={1}>
          Running Admin
        </Typography>
        <Typography variant="body1" color={colors.grey[300]}>
          Accounts and activity, the race catalogue, and the maintenance jobs that
          rebuild stored data.
        </Typography>
      </Box>

      {/* Grouped into tabs: the page had grown to nine stacked panels and ~1,170
          lines, so finding anything meant scrolling past everything else. The
          grouping follows what an admin is actually doing at the time — looking
          at people, curating the race catalogue, or running a maintenance job —
          rather than the order the panels happened to be written in. */}
      <Tabs
        value={adminTab}
        onChange={(e, v) => setAdminTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: `1px solid ${colors.grey[700]}`,
          '& .MuiTab-root': { textTransform: 'none', color: colors.grey[300], minHeight: 44 },
          '& .Mui-selected': { color: `${colors.grey[100]} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[500] },
        }}
      >
        <Tab value="users" icon={<PeopleAltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Users" />
        <Tab value="races" icon={<PlaceIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Races" />
        <Tab value="maintenance" icon={<BuildIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Maintenance" />
      </Tabs>

      {adminTab === 'users' && (
        <>
        {/* ── Every account, at a glance ──────────────────────────────────── */}
        <Paper
          elevation={3}
          sx={{ p: 3, mb: 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={0.5}>
            <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <PeopleAltIcon /> Users
            </Typography>
            <Button
              size="small"
              startIcon={statsLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={loadUserStats}
              disabled={statsLoading}
              sx={{ textTransform: 'none', color: colors.grey[200] }}
            >
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            Lifetime totals per account, newest activity first. Location falls back through
            five sources until one answers — the last session we could place by IP, the last
            run recorded, the nearest named run to its coordinates, the last race predicted
            for, then the email domain — and each row is tagged with the one that did. Hover
            a tag for what it is worth.
          </Typography>

          {/* Totals. Sits above the grid so the shape of the whole base reads before
              any individual row does. */}
          {statsTotals && (
            <Box display="grid" gap={1.5} mb={2}
              sx={{ gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(7,1fr)' } }}>
              {[
                ['Accounts', statsTotals.users],
                ['With activity', statsTotals.with_activity],
                ['Placed', statsTotals.located ?? 0],
                ['FIT uploads', statsTotals.fit_uploads],
                ['Predictions', statsTotals.predictions],
                ['Watch plans', statsTotals.watch_plans],
                ['Garmin linked', statsTotals.garmin],
              ].map(([label, value]) => (
                <Box key={label} sx={{
                  backgroundColor: colors.primary[500], border: `1px solid ${colors.grey[700]}`,
                  borderRadius: '8px', px: 1.5, py: 1.25, minWidth: 0,
                }}>
                  <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}
                    sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                    {value}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[300]} noWrap>{label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {statsLoading && statsRows.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : statsRows.length === 0 ? (
            <Alert severity="info">No users yet.</Alert>
          ) : (
            <DataGrid
              autoHeight
              rows={statsRows}
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              columns={[
                {
                  field: 'email', headerName: 'User', flex: 1.8, minWidth: 210,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0 }}>
                      <Typography variant="caption" color={colors.grey[500]}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}>{params.row.id}</Typography>
                      <Typography variant="caption" color={colors.grey[100]} noWrap
                        title={params.value || ''}>{params.value}</Typography>
                      {params.row.is_admin && (
                        <Chip label="admin" size="small"
                          sx={{ height: 16, fontSize: '0.6rem', backgroundColor: colors.blueAccent[700],
                                color: colors.grey[100] }} />
                      )}
                    </Box>
                  ),
                },
                {
                  field: 'joined', headerName: 'Joined', width: 100,
                  valueFormatter: (v) => (v ? new Date(v).toLocaleDateString() : '—'),
                },
                {
                  field: 'country', headerName: 'Location', flex: 1, minWidth: 160, sortable: true,
                  renderCell: (params) => {
                    const { country, city, location_source: source } = params.row;
                    // Every source exhausted. Rare now — it takes an account with no
                    // runs, no predictions and a generic email — so it reads as
                    // absence rather than as an error.
                    if (!country && !city) {
                      return <Typography variant="caption" color={colors.grey[600]}
                        sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>—</Typography>;
                    }
                    const place = [city, country].filter(Boolean).join(', ');
                    const meta = LOCATION_SOURCES[source];
                    // Only the IP is a reading of where the person was; the rest are
                    // inferences, and the dimmer tag says so at a glance.
                    const tagColor = source === 'ip' ? colors.greenAccent[400]
                      : source === 'run' ? colors.grey[400]
                      : colors.grey[600];
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0 }}>
                        <Typography variant="caption" color={colors.grey[200]} noWrap title={place}>
                          {place}
                        </Typography>
                        {meta && (
                          <Typography variant="caption" noWrap
                            title={`${meta.tag} — ${meta.hint}`}
                            sx={{ fontSize: '0.6rem', color: tagColor, flexShrink: 0, cursor: 'help' }}>
                            {meta.tag}
                          </Typography>
                        )}
                      </Box>
                    );
                  },
                },
                {
                  field: 'last_activity', headerName: 'Last activity', width: 140,
                  renderCell: (params) => {
                    if (!params.value) {
                      return <Typography variant="caption" color={colors.grey[600]}
                        sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>never</Typography>;
                    }
                    const d = new Date(params.value);
                    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
                    // Days-ago is what an admin actually scans for; the date itself is
                    // on hover for when the exact day matters.
                    const label = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
                    return (
                      <Typography variant="caption" title={d.toLocaleString()}
                        color={days <= 7 ? colors.greenAccent[400] : days <= 30 ? colors.grey[200] : colors.grey[500]}
                        sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {label}
                      </Typography>
                    );
                  },
                },
                { field: 'fit_uploads', headerName: 'FIT', width: 70, type: 'number' },
                { field: 'analyses', headerName: 'Analyses', width: 90, type: 'number' },
                { field: 'predictions', headerName: 'Predictions', width: 102, type: 'number' },
                { field: 'watch_plans', headerName: 'Watch plans', width: 104, type: 'number' },
                { field: 'comparisons', headerName: 'Compare', width: 90, type: 'number' },
                {
                  field: 'garmin', headerName: 'Garmin', width: 88, sortable: true,
                  renderCell: (params) => (params.value ? (
                    <Chip icon={<WatchIcon sx={{ fontSize: 12 }} />} label="yes" size="small"
                      title={params.row.garmin_last_push
                        ? `last push ${new Date(params.row.garmin_last_push).toLocaleString()}`
                        : 'connected, nothing pushed yet'}
                      sx={{ height: 18, fontSize: '0.62rem', backgroundColor: colors.greenAccent[700],
                            color: colors.grey[100] }} />
                  ) : (
                    <Typography variant="caption" color={colors.grey[600]}
                      sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>—</Typography>
                  )),
                },
              ]}
              sx={{
                backgroundColor: colors.primary[500], borderColor: colors.grey[700],
                '& .MuiDataGrid-cell': { color: colors.grey[200] },
              }}
            />
          )}
        </Paper>

        {/* ── A user's stored FIT files ──────────────────────────────────── */}
        <Paper
          elevation={3}
          sx={{ p: 3, mt: 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
            User FIT files
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            Every FIT file a user has stored, newest first. Download one to reproduce a
            reported problem against the file itself rather than a description of it.
          </Typography>

          <Box sx={{ maxWidth: 420, mb: 2 }}>
            <Autocomplete
              options={users}
              value={filesUser}
              onChange={(e, v) => handleSelectFilesUser(v)}
              getOptionLabel={(o) => o?.email || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Select user" size="small" />}
            />
          </Box>

          {filesLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : !filesUser ? (
            <Alert severity="info">Select a user to see their FIT files.</Alert>
          ) : userFiles.length === 0 ? (
            <Alert severity="info">This user has no stored FIT files.</Alert>
          ) : (
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" color={colors.grey[300]} mb={1}>
                {userFiles.length} file{userFiles.length === 1 ? '' : 's'}
              </Typography>
              <DataGrid
                autoHeight
                rows={userFiles}
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                columns={[
                  { field: 'filename', headerName: 'File', flex: 1.6, minWidth: 200 },
                  { field: 'race', headerName: 'Race', flex: 1, minWidth: 130,
                    valueGetter: (v) => v || '—' },
                  { field: 'workout_date', headerName: 'Workout', width: 110,
                    valueGetter: (v, row) => (v || row.uploaded_at || '').slice(0, 10) },
                  { field: 'total_distance_km', headerName: 'km', width: 78, type: 'number' },
                  { field: 'total_duration_sec', headerName: 'Duration', width: 96,
                    valueFormatter: (v) => (v ? formatHms(v) : '—') },
                  { field: 'avg_pace', headerName: 'Pace', width: 74, type: 'number' },
                  { field: 'elevation_gain', headerName: 'D+', width: 74, type: 'number' },
                  { field: 'file_size', headerName: 'Size', width: 88,
                    valueFormatter: (v) => (v ? `${(v / 1e6).toFixed(1)} MB` : '—') },
                  {
                    field: 'download', headerName: '', width: 120, sortable: false,
                    filterable: false, disableColumnMenu: true,
                    renderCell: (params) => (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={downloadingId === params.row.id
                          ? <CircularProgress size={14} color="inherit" />
                          : <DownloadIcon fontSize="small" />}
                        disabled={downloadingId != null}
                        onClick={() => handleDownloadUserFile(params.row)}
                        sx={{ textTransform: 'none', color: colors.grey[100],
                              borderColor: colors.grey[600] }}
                      >
                        {downloadingId === params.row.id ? '...' : 'Download'}
                      </Button>
                    ),
                  },
                ]}
                sx={{ backgroundColor: colors.primary[500], borderColor: colors.grey[700] }}
              />
            </Box>
          )}
        </Paper>

        {/* Personalised-beta history inspector */}
        <Paper
          elevation={3}
          sx={{ p: 3, mb: 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
            Beta history
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            Personalised Riegel-β fits recorded on multi-file predictions (Mode 2 &amp; 3).
            Data-gathering only — these do not change predictions. Pick a user to inspect.
          </Typography>

          <Box sx={{ maxWidth: 420, mb: 2 }}>
            <Autocomplete
              options={users}
              value={selectedUser}
              onChange={(e, v) => handleSelectBetaUser(v)}
              getOptionLabel={(o) => o?.email || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Select user" size="small" />}
            />
          </Box>

          {betaLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : selectedUser ? (
            betaRows.length > 0 ? (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  autoHeight
                  rows={betaRows}
                  columns={betaColumns}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={{ backgroundColor: colors.primary[500], borderColor: colors.grey[700] }}
                />
              </Box>
            ) : (
              <Alert severity="info">No beta history for this user yet.</Alert>
            )
          ) : (
            <Alert severity="info">Select a user to see their beta history.</Alert>
          )}
        </Paper>
        </>
      )}

      {adminTab === 'races' && (
        <>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: colors.grey[800],
            borderRadius: '8px'
          }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={3}>
            Race GPX Upload
          </Typography>

          {/* Race Selection */}
          <Box mb={3}>
            <Typography variant="body2" color={colors.grey[300]} mb={1}>
              Select Race *
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setRacesDrawerOpen(true)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                color: colors.grey[100],
                borderColor: colors.primary[400],
                textTransform: 'none',
                '&:hover': {
                  borderColor: colors.primary[300],
                  backgroundColor: colors.primary[700]
                }
              }}
            >
              {selectedRace
                ? `${selectedRace.name} - ${selectedRace.distance_km}km (${selectedRace.event_name})`
                : 'Select a race...'}
            </Button>
            {selectedRace && (
              <Box mt={1}>
                <Chip
                  label={selectedRace.has_gpx ? "Has GPX" : "No GPX"}
                  icon={selectedRace.has_gpx ? <CheckCircleIcon /> : <CancelIcon />}
                  color={selectedRace.has_gpx ? "success" : "default"}
                  size="small"
                />
              </Box>
            )}
          </Box>

          {/* File Upload */}
          <Box mb={3}>
            <Typography variant="body2" color={colors.grey[300]} mb={1}>
              GPX File *
            </Typography>
            <input
              id="gpx-file-input"
              type="file"
              accept=".gpx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="gpx-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.primary[400],
                  '&:hover': {
                    borderColor: colors.primary[300],
                    backgroundColor: colors.primary[700]
                  }
                }}
              >
                Choose GPX File
              </Button>
            </label>
            {uploadFile && (
              <Typography variant="body2" color={colors.greenAccent[500]} mt={1}>
                Selected: {uploadFile.name}
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleUploadGpx}
              disabled={!selectedRace || !uploadFile || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <UploadFileIcon />}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.greenAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              Upload GPX
            </Button>

            <Button
              variant="contained"
              onClick={handleDeleteGpx}
              disabled={!selectedRace || !selectedRace.has_gpx || loading}
              startIcon={<DeleteIcon />}
              sx={{
                backgroundColor: colors.redAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.redAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              Delete GPX
            </Button>

            <Button
              variant="outlined"
              onClick={handleDeleteRace}
              disabled={!selectedRace || loading}
              startIcon={<DeleteIcon />}
              sx={{
                borderColor: colors.redAccent[400],
                color: colors.redAccent[400],
                '&:hover': {
                  borderColor: colors.redAccent[300],
                  backgroundColor: colors.redAccent[900]
                },
                '&:disabled': {
                  borderColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              Delete Race
            </Button>
          </Box>
        </Paper>

        {/* Import Races from JSON */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 3,
            backgroundColor: colors.grey[800],
            borderRadius: '8px'
          }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
            Import Races from JSON
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mb={3}>
            Upload a races JSON file to add new races or update existing ones. Races are matched by event name + race name.
          </Typography>

          <Box mb={3}>
            <Typography variant="body2" color={colors.grey[300]} mb={1}>
              JSON File *
            </Typography>
            <input
              id="import-json-input"
              type="file"
              accept=".json"
              onChange={handleImportFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="import-json-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.primary[400],
                  '&:hover': {
                    borderColor: colors.primary[300],
                    backgroundColor: colors.primary[700]
                  }
                }}
              >
                Choose JSON File
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" color={colors.greenAccent[500]} mt={1}>
                Selected: {importFile.name}
              </Typography>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={handleImportRaces}
              disabled={!importFile || importLoading}
              startIcon={importLoading ? <CircularProgress size={20} /> : <PublishIcon />}
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.blueAccent[700]
                },
                '&:disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500]
                }
              }}
            >
              Import Races
            </Button>

            {importResult && (
              <Alert severity="success" sx={{ flex: 1 }}>
                Import complete: <strong>{importResult.added}</strong> added, <strong>{importResult.updated}</strong> updated ({importResult.total} total)
              </Alert>
            )}
          </Box>
        </Paper>

        {/* ── Checkpoint coordinate audit ─────────────────────────────────── */}
        <Paper
          elevation={3}
          sx={{ p: 3, mt: 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
            Checkpoint coordinates
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            Every checkpoint says where it is twice — a distance from the start and a pair of
            coordinates — and when those disagree, stops get attributed to the wrong aid station
            and plans split in the wrong place. This checks each one against its own race&apos;s GPX
            and snaps the wrong ones onto the track at their stated distance. Each race is judged
            against its own drift, so a course that measures 1% short is not condemned for it.
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={auditDryRun}
                  onChange={(e) => setAuditDryRun(e.target.checked)}
                  sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[400] } }}
                />
              }
              label={<Typography variant="body2" color={colors.grey[200]}>Dry run</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={auditDropGpx}
                  onChange={(e) => setAuditDropGpx(e.target.checked)}
                  sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.redAccent[400] } }}
                />
              }
              label={
                <Typography variant="body2" color={colors.grey[200]}>
                  Also detach a GPX that is the wrong course
                </Typography>
              }
            />
            <Button
              variant="contained"
              startIcon={auditLoading ? <CircularProgress size={16} color="inherit" /> : <PlaceIcon />}
              onClick={handleAuditCheckpoints}
              disabled={auditLoading}
              sx={{
                backgroundColor: auditDryRun ? colors.blueAccent[700] : colors.redAccent[600],
                textTransform: 'none',
                '&:hover': { backgroundColor: auditDryRun ? colors.blueAccent[600] : colors.redAccent[500] },
              }}
            >
              {auditLoading ? 'Checking...' : auditDryRun ? 'Audit' : 'Audit and snap'}
            </Button>
          </Box>

          {auditResult && (
            <Box>
              <Alert severity={auditResult.dry_run ? 'info' : 'success'} sx={{ mb: 2 }}>
                {auditResult.dry_run ? 'Dry run — nothing was written. ' : 'Written. '}
                {auditResult.races_clean} of {auditResult.races_audited} races clean.
                {' '}{auditResult.checkpoints_snapped} checkpoint(s) to snap across
                {' '}{auditResult.races_with_fixes} race(s).
                {auditResult.races_with_wrong_gpx > 0 && (
                  <> {auditResult.races_with_wrong_gpx} race(s) carry the wrong GPX entirely —
                    their checkpoints are left alone.</>
                )}
              </Alert>
              <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
                <DataGrid
                  autoHeight
                  density="compact"
                  hideFooter={(auditResult.races || []).length <= 100}
                  rows={(auditResult.races || []).flatMap((r, ri) =>
                    (r.findings || []).length
                      ? r.findings.map((f, fi) => ({
                          id: `${ri}-${fi}`, race: r.race, verdict: r.verdict, scale: r.scale,
                          cp: f.cp, issue: f.issue, stated_km: f.stated_km,
                          found_km: f.gpx_pos_km ?? null, snap_km: f.expected_km,
                        }))
                      : [{ id: `${ri}`, race: r.race, verdict: r.verdict, scale: r.scale,
                           cp: '—', issue: '—', stated_km: r.stated_km, found_km: r.gpx_km, snap_km: null }]
                  )}
                  columns={[
                    { field: 'race', headerName: 'Race', flex: 1.2 },
                    { field: 'verdict', headerName: 'Verdict', width: 130 },
                    { field: 'cp', headerName: 'Checkpoint', flex: 1 },
                    { field: 'issue', headerName: 'Issue', width: 130 },
                    { field: 'scale', headerName: 'Race scale', width: 105, type: 'number' },
                    { field: 'stated_km', headerName: 'Stated km', width: 100, type: 'number' },
                    { field: 'found_km', headerName: 'Found at', width: 95, type: 'number' },
                    { field: 'snap_km', headerName: 'Snap to', width: 90, type: 'number' },
                  ]}
                  sx={{ border: 'none', '& .MuiDataGrid-cell': { color: colors.grey[200] } }}
                />
              </Box>
            </Box>
          )}
        </Paper>
        </>
      )}

      {adminTab === 'maintenance' && (
        <>
        {/* ── Re-analyse stored sessions ─────────────────────────────────── */}
        <Paper
          elevation={3}
          sx={{ p: 3, mt: 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}
        >
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
            Re-analyse FIT files
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            Re-runs the analysis pipeline over every stored session and overwrites the saved
            result. Needed after a change to what the parser measures — stored sessions keep
            their old numbers until this runs, and predictions read from them. Each file is
            re-analysed against its own linked race, so checkpoint handling matches that course.
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            A full pass takes minutes and can outlive the proxy's patience, so on a large
            database set a <strong>Limit</strong> — 10 to 20 is comfortable — and press the
            button repeatedly. <strong>Start at</strong> advances on its own after each batch
            and empties when there is nothing left. Every session is committed as it finishes,
            so a run that dies part-way keeps everything it had already done.
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reanalyzeDryRun}
                  onChange={(e) => setReanalyzeDryRun(e.target.checked)}
                  sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[400] } }}
                />
              }
              label={
                <Typography variant="body2" color={colors.grey[200]}>
                  Dry run (report what would change, write nothing)
                </Typography>
              }
            />
            <TextField
              size="small"
              label="Limit"
              placeholder="all"
              value={reanalyzeLimit}
              onChange={(e) => setReanalyzeLimit(e.target.value.replace(/[^0-9]/g, ''))}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label="Start at"
              placeholder="0"
              value={reanalyzeOffset}
              onChange={(e) => setReanalyzeOffset(e.target.value.replace(/[^0-9]/g, ''))}
              sx={{ width: 110 }}
            />
            <Button
              variant="contained"
              startIcon={reanalyzeLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleReanalyzeAll}
              disabled={reanalyzeLoading}
              sx={{
                backgroundColor: reanalyzeDryRun ? colors.blueAccent[700] : colors.redAccent[600],
                textTransform: 'none',
                '&:hover': { backgroundColor: reanalyzeDryRun ? colors.blueAccent[600] : colors.redAccent[500] },
              }}
            >
              {reanalyzeLoading
                ? 'Working...'
                : reanalyzeDryRun ? 'Dry run' : 'Re-analyse and overwrite'}
            </Button>
          </Box>

          {reanalyzeResult && (
            <Box>
              <Alert severity={reanalyzeResult.dry_run ? 'info' : 'success'} sx={{ mb: 2 }}>
                {reanalyzeResult.dry_run ? 'Dry run — nothing was written. ' : 'Written. '}
                {reanalyzeResult.reanalyzed} of {reanalyzeResult.sessions_considered} sessions
                re-analysed, {reanalyzeResult.skipped_no_file} skipped (file gone),
                {' '}{reanalyzeResult.failed} failed.
                {' '}{reanalyzeResult.stop_time_reclassified_min} min of stop time reclassified.
                {reanalyzeResult.total_matching != null && (
                  <> {' '}Batch started at {reanalyzeResult.offset} of{' '}
                    {reanalyzeResult.total_matching}.{' '}
                    {reanalyzeResult.next_offset == null
                      ? 'Nothing left — the whole set is done.'
                      : `${reanalyzeResult.total_matching - reanalyzeResult.next_offset} still to go; press again.`}
                  </>
                )}
              </Alert>
              <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                <DataGrid
                  autoHeight
                  density="compact"
                  hideFooter={(reanalyzeResult.details || []).length <= 100}
                  rows={(reanalyzeResult.details || []).map((d, i) => ({ id: i, ...d }))}
                  columns={[
                    { field: 'file', headerName: 'File', flex: 1.4 },
                    { field: 'race', headerName: 'Race', flex: 1 },
                    { field: 'paused_before_min', headerName: 'Stops before', width: 118, type: 'number' },
                    { field: 'paused_after_min', headerName: 'Stops after', width: 110, type: 'number' },
                    { field: 'cp_stop_before_min', headerName: 'At CPs before', width: 125, type: 'number' },
                    { field: 'cp_stop_after_min', headerName: 'At CPs after', width: 118, type: 'number' },
                    { field: 'stage2_windows', headerName: 'CP windows', width: 112, type: 'number' },
                  ]}
                  sx={{ border: 'none', '& .MuiDataGrid-cell': { color: colors.grey[200] } }}
                />
              </Box>
            </Box>
          )}
        </Paper>

        {/* FIT File Combiner */}
        <Box mt={3}>
          <FitFileCombiner />
        </Box>
        </>
      )}

      {/* Races Drawer */}
      <WorkoutPickerDrawer
        open={racesDrawerOpen}
        onClose={() => setRacesDrawerOpen(false)}
        onSelect={handleSelectRace}
        title="Select Race"
        tabs={['races']}
        actionLabel="Select"
      />



      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RunningAdmin;
